import type { IntelligenceTopicRecord } from "@graftus/schema";

type TranslateFn = (key: string) => string;

/** Replaced presets; still accepted when loaded from persisted threads. */
export const LEGACY_TOPICS = [
  {
    key: "strengths",
    title: "Styrkor",
    starter: "Vilka är de viktigaste styrkorna att lyfta fram?",
  },
  {
    key: "risks",
    title: "Risker",
    starter:
      "Vilka utvecklingsområden eller risker ser du, och hur bör de hanteras?",
  },
  {
    key: "collaboration",
    title: "Samarbete",
    starter:
      "Hur fungerar samarbete och kommunikation, och vad bör chefen tänka på?",
  },
  {
    key: "coaching",
    title: "Coaching-plan",
    starter: "Föreslå en konkret coaching-plan för de kommande 90 dagarna.",
  },
  {
    key: "action_plan",
    title: "Handlingsplan",
    starter:
      "Föreslå en konkret handlingsplan för chefen de kommande 30–90 dagarna.",
  },
] as const;

/**
 * Hardcoded fallback used while the dynamic topic list is loading. Once the
 * tRPC query resolves, the server-resolved topics take precedence.
 */
export const DEFAULT_TOPICS: IntelligenceTopicRecord[] = [
  {
    key: "kpis",
    title: "KPIer",
    starter:
      "Vilka KPI:er är mest relevanta att följa upp här, och vad säger underlaget idag?",
    promptHint: "",
  },
  {
    key: "wellbeing",
    title: "Välmående",
    starter:
      "Hur ser välmående och belastning ut, och vad bör chefen prioritera?",
    promptHint: "",
  },
  {
    key: "team_chemistry",
    title: "Teamkemi",
    starter:
      "Hur ser teamdynamik och samarbete ut — styrkor, risker och nästa steg?",
    promptHint: "",
  },
  {
    key: "development",
    title: "Utveckling",
    starter:
      "Vilka utvecklingsområden är viktigast närmaste tiden, utifrån test och kontext?",
    promptHint: "",
  },
];

/** Resolved server topics, or {@link DEFAULT_TOPICS} while loading / when all are hidden. */
export function effectiveTopics(
  dynamicTopics?: IntelligenceTopicRecord[],
): IntelligenceTopicRecord[] {
  return dynamicTopics?.length ? dynamicTopics : DEFAULT_TOPICS;
}

/** Localized version of DEFAULT_TOPICS using the provided translation function. */
export function getLocalizedDefaultTopics(t: TranslateFn): IntelligenceTopicRecord[] {
  return DEFAULT_TOPICS.map((topic) => ({
    ...topic,
    title: t(`topics.${topic.key}.title`),
    starter: t(`topics.${topic.key}.starter`),
  }));
}

/** Like {@link effectiveTopics} but translates topics by key when translations exist. */
export function getLocalizedEffectiveTopics(
  t: TranslateFn,
  dynamicTopics?: IntelligenceTopicRecord[],
): IntelligenceTopicRecord[] {
  const topics = dynamicTopics?.length ? dynamicTopics : DEFAULT_TOPICS;
  return topics.map((topic) => {
    const titleKey = `topics.${topic.key}.title`;
    const starterKey = `topics.${topic.key}.starter`;
    const translatedTitle = t(titleKey);
    const translatedStarter = t(starterKey);
    return {
      ...topic,
      title: translatedTitle !== titleKey ? translatedTitle : topic.title,
      starter: translatedStarter !== starterKey ? translatedStarter : topic.starter,
    };
  });
}

export type LegacyTopicKey = (typeof LEGACY_TOPICS)[number]["key"];
export type CurrentTopicKey = string;
export type TopicKey = string;

export type TopicEntry = IntelligenceTopicRecord;

const LEGACY_KEYS = new Set<string>(LEGACY_TOPICS.map((t) => t.key));

export function isRecognizedTopicKey(
  key: string,
  dynamicTopics?: IntelligenceTopicRecord[],
): boolean {
  if (LEGACY_KEYS.has(key)) return true;
  return effectiveTopics(dynamicTopics).some((t) => t.key === key);
}

export function isCurrentTopicKey(
  key: string,
  dynamicTopics?: IntelligenceTopicRecord[],
): boolean {
  return effectiveTopics(dynamicTopics).some((t) => t.key === key);
}

export function getTopic(
  key: string,
  dynamicTopics?: IntelligenceTopicRecord[],
): TopicEntry {
  const topics = effectiveTopics(dynamicTopics);
  const found = topics.find((t) => t.key === key);
  if (found) return found;
  const legacy = LEGACY_TOPICS.find((t) => t.key === key);
  if (legacy) return { ...legacy, promptHint: "" };
  return topics[0] ?? DEFAULT_TOPICS[0]!;
}
